<template>
  <div class="report-list">
    <s-table
      class="report-list__table"
      :items="reports"
      :fields="fields"
      :sort-by="sortBy.field"
      :sort-desc="sortBy.desc"
      :busy="loading"
      @sort-changed="sort($event)"
    >
      <template #cell(installations)="data">
        <list-aggregator
          suppressed-label-path="reports.list.suppressed-installations"
          :modal-title="data.item.reportName"
          :modal-table-items="data.item.installations"
          :modal-table-header="$t('entities.installation')"
        />
      </template>
      <template #cell(assets)="data">
        <list-aggregator
          suppressed-label-path="reports.list.suppressed-assets"
          :modal-title="data.item.reportName"
          :modal-table-items="data.item.assets"
          :modal-table-header="$t('entities.asset')"
        />
      </template>
      <template #cell(actions)="data">
        <router-link
          target="_blank"
          :to="getReportRoute(data.item.id)"
        >
          <b-button
            size="sm"
            variant="primary"
          >
            {{ $t('general.view') }}
          </b-button>
        </router-link>
        <b-dropdown
          class="report-list__options-btn"
          right
          no-caret
          offset="-15"
          variant="link"
          boundary="window"
        >
          <template slot="button-content">
            <s-loader v-if="isReportLoading(data.item.id)"/>
            <i
              v-else
              aria-hidden="true"
              class="fas fa-ellipsis-v text-gray"
            />
          </template>
          <b-dropdown-item
            v-for="({
              label,
              itemClass,
              iconClass,
              click,
            }) in dropdownItems"
            :key="label"
            :class="itemClass"
            @click="click(data.item)"
          >
            <span
              class="report-list__option-icon text-center"
              :class="iconClass"
            />
            {{ label }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </s-table>
    <grid-paginator
      v-model="pageHandler"
      :per-page="pagination.pageSize"
      :total-rows="pagination.totalRows"
    />
  </div>
</template>

<script>
import GridPaginator from '@/components/lists/Paginator';
import ListAggregator from '@/components/listAggregator';
import OscillographyService from '@/domain/services/generalPanel/reports/oscillography';
import { alertModes } from '@/services/messages/alertService';

export default {
  components: {
    GridPaginator,
    ListAggregator,
  },
  data: () => ({
    loadingDropdown: '',
  }),
  props: {
    searchTerm: {
      type: String,
    },
    reports: {
      type: Array,
    },
    loading: {
      type: Boolean,
    },
    sortBy: {
      type: Object,
      default: () => ({}),
    },
    pagination: {
      type: Object,
      default: () => ({
        perPage: 200,
        currentPage: 1,
        totalRows: null,
      }),
    },
  },
  computed: {
    pageHandler: {
      get() {
        return this.pagination.currentPage;
      },
      set(newPage) {
        this.$emit('page-changed', newPage);
      },
    },
    dropdownItems() {
      return [
        {
          itemClass: 'report-list__option-export',
          iconClass: 'fas fa-file-excel',
          label: this.$t('general.export-xlsx'),
          click: oscillography => this.exportToXlsx(oscillography),
        },
        {
          itemClass: 'report-list__option-edit',
          iconClass: 'fas fa-pencil-alt',
          label: this.$t('reports.edit-report'),
          click: oscillography => this.editReport(oscillography),
        },
        {
          itemClass: 'report-list__option-delete',
          iconClass: 'fas fa-trash-alt',
          label: this.$t('reports.delete-report'),
          click: oscillography => this.delete(oscillography),
        },
      ];
    },
    fields() {
      return [
        {
          key: 'reportName',
          sortable: true,
          labelPath: 'report-name',
          class: 'report-list__cell-report-name',
          label: this.$t('reports.report-name'),
        },
        {
          key: 'installations',
          sortable: false,
          labelPath: 'installations',
          label: this.$t('entities.installation'),
          class: 'truncate report-list__cell-installation',
        },
        {
          key: 'assetType',
          sortable: true,
          labelPath: 'asset-type',
          class: 'report-list__cell-asset-type',
          label: this.$t('fields.asset.asset-type'),
        },
        {
          key: 'assets',
          sortable: false,
          labelPath: 'assets',
          class: 'report-list__cell-asset',
          label: this.$t('entities.asset'),
        },
        {
          key: 'actions',
          sortable: false,
          labelPath: null,
          label: '',
          class: 'report-list__cell-actions',
        },
      ];
    },
  },
  methods: {
    isReportLoading(id) {
      return this.loadingDropdown === id;
    },
    editReport({ id }) {
      this.$router.push({
        name: 'reports.oscillography.edit-report',
        params: {
          relatorioId: id,
        },
      });
    },
    removeReport(id) {
      return OscillographyService.removeReport(id).then(() => {
        this.$pushAlert({
          message: 'reports.report-successfully-excluded',
          mode: alertModes.TOAST,
          type: 'success',
        });
        this.$emit('refresh-list');
      });
    },
    delete({ id }) {
      this.$confirmMessage({ code: 'MC021' })
        .accepted(() => this.removeReport(id));
    },
    sort(event) {
      this.$emit('sort-changed', event);
    },
    getReportRoute: reportId => ({
      name: 'clean-view.oscillography.view-report',
      params: {
        reportId,
      },
    }),
    exportToXlsx({ id }) {
      this.loadingDropdown = id;
      OscillographyService.downloadReport(id).finally(() => {
        this.loadingDropdown = '';
      });
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"

.report-list
  width: 100%
  ::v-deep
    .btn.dropdown-toggle.btn-link.dropdown-toggle-no-caret:hover,
    .btn.dropdown-toggle.btn-link.dropdown-toggle-no-caret:focus
      text-decoration: none
  &__table
      flex: 1
      min-height: 48vh
  &__options-btn
    margin: -10px 0
  &__option-icon
    margin-right: 5px
    width: 1em
  ::v-deep &__option
    &-export a
      color: var(--color-primary)
    &-delete a
      color: var(--color-danger)
  ::v-deep &__cell
    &-report-name
      min-width: 100px
      width: 20%
    &-installations
      width: 25%
    &-asset-type
      width: 25%
    &-asset
      width: 10%
    &-actions
      min-width: 15em
      width: 15%
</style>
